import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import press24 from "@/assets/press-24.png.asset.json";
import press25 from "@/assets/press-25.png.asset.json";
import press26 from "@/assets/press-26.png.asset.json";
import press27 from "@/assets/press-27.png.asset.json";
import press28 from "@/assets/press-28.png.asset.json";
import pressAbc from "@/assets/press-abc-sergi.png.asset.json";
import sergiMoure from "@/assets/sergi-moure.png.asset.json";
import pressIndustrial from "@/assets/press-industrial-almacen.png.asset.json";
import brand51 from "@/assets/brand-51.png.asset.json";
import brand52 from "@/assets/brand-52.png.asset.json";
import brand53 from "@/assets/brand-53.png.asset.json";
import brand54 from "@/assets/brand-54.png.asset.json";
import brandAnim from "@/assets/brand-anim.gif.asset.json";
import pressBigfishPrat from "@/assets/press-bigfish-prat.png.asset.json";

type PressItem = { outlet: string; date: string; titleKey: string; href: string; image: string; images?: string[] };

const rawItems: PressItem[] = [
  {
    outlet: "ABC",
    date: "16 Oct 2026",
    titleKey: "item10Desc",
    href: "https://www.abc.es/espana/cataluna/sergi-moure-barcelona-reto-convertir-tecnologia-industria-20261016121957-nt.html",
    image: pressAbc.url,
  },
  {
    outlet: "Revista Aral",
    date: "Oct 2026",
    titleKey: "item9Desc",
    href: "https://www.revistaaral.com/texto-diario/mostrar/6049748/big-fish-entra-travel-retail-tienda-automatizada-barcelona-prat",
    image: pressBigfishPrat.url,
  },
  {
    outlet: "Interempresas",
    date: "25 Ago 2026",
    titleKey: "item7Desc",
    href: "https://www.interempresas.net/Estaciones-servicio/661555-Techtail-lanza-su-division-Industrial-para-automatizar-operaciones-fisicas-de-industria.html",
    image: pressIndustrial.url,
  },
  {
    outlet: "TECHTAIL",
    date: "Ago 2026",
    titleKey: "item8Desc",
    href: "https://www.linkedin.com/posts/techtail-company_techtail-ha-cambiado-mucho-durante-los-%C3%BAltimos-activity-7496492875464032256-w71s",
    image: brand51.url,
    images: [brand51.url, brandAnim.url, brand52.url, brand54.url, brand53.url],
  },

  {
    outlet: "Autónomos y Emprendedor",
    date: "Jul 2026",
    titleKey: "item5Desc",
    href: "https://www.autonomosyemprendedor.es/opinion/sergi-moure/competir-precio-basta-experiencia-decide/20260714190426054967.html",
    image: sergiMoure.url,
  },
  {
    outlet: "Adnkronos",
    date: "Jun 2026",
    titleKey: "item6Desc",
    href: "https://www.adnkronos.com/economia/wmf-2026-a-bolognafiere-il-primo-negozio-gestito-dallia-puo-funzionare-senza-personale_1hQKoSLwRDqTFSiB3ySJNu",
    image: press27.url,
  },
  {
    outlet: "Grupo Moure",
    date: "Feb 2026",
    titleKey: "item3Desc",
    href: "https://grupomoure.com/2026/02/23/techtail-presenta-su-flex-store-en-euroshop-y-refuerza-el-avance-del-retail-autonomo-a-nivel-internacional/",
    image: press25.url,
  },
  {
    outlet: "Solo Industria",
    date: "Mar 2026",
    titleKey: "item4Desc",
    href: "https://soloindustria.com/techtail-presenta-el-formato-corner-una-solucion-de-retail-autonomo-en-el-stand-de-huawei-del-mwc-2026/",
    image: press24.url,
  },
  {
    outlet: "Innova Spain",
    date: "Mar 2026",
    titleKey: "item1Desc",
    href: "https://www.innovaspain.com/techtail-tienda-automatizada-huawei-mwc-claudia-puig/",
    image: press28.url,
  },
  {
    outlet: "TNR News",
    date: "2026",
    titleKey: "item2Desc",
    href: "https://tnrnews.es/lo-que-viene/techtail-cuando-prima-la-inmediatez-y-la-comodidad/",
    image: press26.url,
  },
];

const PressImage = ({ images, alt }: { images: string[]; alt: string }) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (images.length < 2) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % images.length), 2200);
    return () => clearInterval(id);
  }, [images.length]);

  return (
    <div className="relative aspect-[4/3] w-full overflow-hidden">
      {images.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={alt}
          loading="lazy"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 group-hover:scale-[1.03] ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
    </div>
  );
};

const PixelArrow = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M2 9h11M9 4l5 5-5 5" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const PressSection = () => {
  const { t } = useTranslation();
  const items = rawItems.map((item) => ({
    ...item,
    title: t(`sHome.pressSection.${item.titleKey}`),
  }));

  return (
    <section id="prensa" className="bg-background py-8 md:py-12">
      <div className="container mx-auto px-4 md:px-6">
        <h2 className="max-w-2xl font-serif text-xl font-normal leading-[1.08] tracking-[-0.01em] text-foreground sm:text-2xl md:text-3xl">
          {t("sHome.pressSection.title")}
        </h2>

        <div className="-mx-4 mt-4 md:-mx-6 md:mt-6">
          <div className="flex snap-x snap-mandatory scroll-pl-4 gap-2.5 overflow-x-auto px-4 pb-3 md:scroll-pl-6 md:gap-3 md:px-6">
            {items.map((item, i) => (

            <a
              key={`${item.href}-${i}`}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex w-[62vw] shrink-0 snap-start flex-col rounded-none bg-bone text-black sm:w-[32vw] lg:w-[17vw]"
            >
              <PressImage images={item.images ?? [item.image]} alt={item.title} />
              <div className="flex flex-1 flex-col justify-between p-3">
                <div>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-dot text-[0.55rem] uppercase tracking-widest text-black/70">
                      {item.outlet}
                    </span>
                    <span className="text-[0.55rem] text-black/40">{item.date}</span>
                  </div>
                  <h3 className="mt-1.5 font-serif text-[0.9rem] font-normal leading-snug md:text-[0.95rem]">
                    {item.title}
                  </h3>
                </div>
                <span className="mt-3 inline-flex items-center gap-1.5 text-[0.7rem] text-black/60 transition-opacity group-hover:opacity-100">
                  <PixelArrow />
                  {t("sHome.pressSection.readMore")}
                </span>
              </div>
            </a>
            ))}
          </div>
        </div>
      </div>
    </section>

  );
};

export default PressSection;
